import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaInbox, FaSearch, FaPlus } from 'react-icons/fa';
import { anfrageService } from '../services/api';
import { SERVICE_LABEL, datumKurz } from '../utils/mailVorlagen';
import '../styles/Anfragen.css';

export const statusKlasse = (s) => ({
  'Neu': 'neu',
  'Beantwortet': 'angefragt',
  'Videocall': 'angefragt',
  'Angebot verschickt': 'versendet',
  'Angenommen': 'bestaetigt',
  'Vertrag verschickt': 'versendet',
  'Gebucht': 'abgeschlossen',
  'Abgesagt': 'zurueck'
}[s] || 'neu');

function eingangText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

const QUELLEN = ['Telefon', 'WhatsApp', 'Instagram', 'Facebook', 'E-Mail', 'Empfehlung', 'Persönlich', 'Sonstiges'];
const LEER = { service: 'hochzeits-dj', vorname: '', nachname: '', email: '', telefon: '', eventart: '', datum: '', ort: '', gaeste: '', quelle: 'Telefon', nachricht: '' };

// Anfrage von Hand anlegen (Telefon, WhatsApp, Instagram …)
function NeueAnfrage({ onAbbrechen }) {
  const navigate = useNavigate();
  const [d, setD] = useState(LEER);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const feld = (label, k, props = {}) => (
    <div className="form-group">
      <label>{label}</label>
      <input className="form-input" value={d[k]} onChange={set(k)} {...props} />
    </div>
  );
  const speichern = async (e) => {
    e.preventDefault();
    if (!d.vorname.trim() && !d.nachname.trim()) return setFehler('Bitte einen Namen angeben.');
    if (!d.email.trim() && !d.telefon.trim()) return setFehler('Bitte E-Mail oder Telefon angeben.');
    setBusy(true); setFehler('');
    try {
      const { anfrage } = await anfrageService.create(d);
      navigate(`/anfragen/${anfrage.id}`);
    } catch (err) {
      setFehler(err.response?.data?.error || 'Anfrage konnte nicht angelegt werden.');
      setBusy(false);
    }
  };
  return (
    <form className="card neu-anfrage mb-3" onSubmit={speichern}>
      <h2>Neue Anfrage</h2>
      <div className="form-row">
        <div className="form-group">
          <label>Bereich</label>
          <select className="form-select" value={d.service} onChange={set('service')}>
            <option value="hochzeits-dj">Hochzeits-DJ</option>
            <option value="dj">DJ</option>
            <option value="livemusik">Livemusik</option>
          </select>
        </div>
        <div className="form-group">
          <label>Woher kam die Anfrage?</label>
          <select className="form-select" value={d.quelle} onChange={set('quelle')}>
            {QUELLEN.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">{feld('Vorname', 'vorname', { autoFocus: true })}{feld('Nachname', 'nachname')}</div>
      <div className="form-row">{feld('E-Mail', 'email', { type: 'email' })}{feld('Telefon', 'telefon', { type: 'tel' })}</div>
      <div className="form-row">{feld('Anlass', 'eventart', { placeholder: 'z. B. Hochzeit, Geburtstag' })}{feld('Datum', 'datum', { type: 'date' })}</div>
      <div className="form-row">{feld('Ort', 'ort')}{feld('Gäste', 'gaeste')}</div>
      <div className="form-group">
        <label>Nachricht / Notiz</label>
        <textarea className="form-input" rows="3" value={d.nachricht} onChange={set('nachricht')} />
      </div>
      {fehler && <p className="text-error mb-2">{fehler}</p>}
      <div className="button-row">
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Speichere …' : 'Anfrage anlegen'}</button>
        <button className="btn btn-secondary" type="button" onClick={onAbbrechen}>Abbrechen</button>
      </div>
    </form>
  );
}

function AnfragenList() {
  const [daten, setDaten] = useState({ anfragen: [], status: [] });
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [params, setParams] = useSearchParams();
  const filter = params.get('status') || 'offen';
  const service = params.get('service') || '';
  const [suche, setSuche] = useState('');
  const [neu, setNeu] = useState(false);

  useEffect(() => {
    anfrageService.getAll()
      .then(setDaten)
      .catch((e) => setFehler(e.response?.data?.error || 'Anfragen konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  const zaehler = useMemo(() => {
    const z = {};
    daten.anfragen.forEach(a => { z[a.status] = (z[a.status] || 0) + 1; });
    return z;
  }, [daten]);
  const offen = daten.anfragen.filter(a => !['Gebucht', 'Abgesagt'].includes(a.status)).length;

  const liste = daten.anfragen.filter(a => {
    if (filter === 'offen' && ['Gebucht', 'Abgesagt'].includes(a.status)) return false;
    if (filter !== 'offen' && filter !== 'alle' && a.status !== filter) return false;
    if (service && a.service !== service) return false;
    if (suche) {
      const t = `${a.vorname} ${a.nachname} ${a.email} ${a.ort} ${a.eventart}`.toLowerCase();
      if (!t.includes(suche.toLowerCase())) return false;
    }
    return true;
  });

  const setze = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };

  if (laden) return <div className="spinner"></div>;

  return (
    <div className="anfragen-page">
      <div className="page-header">
        <div>
          <h1><FaInbox /> Anfragen</h1>
          <p className="text-gray">Anfragen von der Website und von dir angelegte. Offen: {offen}</p>
        </div>
        {!neu && <button className="btn btn-primary" onClick={() => setNeu(true)}><FaPlus /> Neue Anfrage</button>}
      </div>

      {neu && <NeueAnfrage onAbbrechen={() => setNeu(false)} />}

      {fehler && <div className="card mb-3 text-error">{fehler}</div>}

      <div className="pipeline-tabs">
        <button className={`pipeline-tab ${filter === 'offen' ? 'active' : ''}`} onClick={() => setze('status', '')}>Offen <span>{offen}</span></button>
        {daten.status.map(s => (
          <button key={s} className={`pipeline-tab ${filter === s ? 'active' : ''}`} onClick={() => setze('status', s)}>
            {s} <span>{zaehler[s] || 0}</span>
          </button>
        ))}
        <button className={`pipeline-tab ${filter === 'alle' ? 'active' : ''}`} onClick={() => setze('status', 'alle')}>Alle <span>{daten.anfragen.length}</span></button>
      </div>

      <div className="anfragen-filter">
        <div className="suche">
          <FaSearch />
          <input className="form-input" placeholder="Name, E-Mail, Ort …" value={suche} onChange={(e) => setSuche(e.target.value)} />
        </div>
        <select className="form-select" value={service} onChange={(e) => setze('service', e.target.value)}>
          <option value="">Alle Bereiche</option>
          {Object.entries(SERVICE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="card anfragen-tabelle">
        {liste.length === 0 ? (
          <p className="text-gray text-center">Keine Anfragen in dieser Ansicht.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Bereich</th><th>Eventdatum</th><th>Ort</th><th>Eingegangen</th><th>Status</th></tr>
            </thead>
            <tbody>
              {liste.map(a => (
                <tr key={a.id}>
                  <td><Link to={`/anfragen/${a.id}`} className="anfrage-name">{a.vorname} {a.nachname}</Link><div className="text-gray klein">{a.eventart}</div></td>
                  <td>{SERVICE_LABEL[a.service] || a.service || '–'}</td>
                  <td>{datumKurz(a.datum) || '–'}</td>
                  <td>{a.ort || '–'}</td>
                  <td className="text-gray">{eingangText(a.eingegangen)}</td>
                  <td><span className={`status-badge status-${statusKlasse(a.status)}`}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AnfragenList;
